import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { proxyListAPI } from '../services/api';

function ProxyListsList() {
  const [proxyLists, setProxyLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTags();
    fetchProxyLists();
  }, [selectedTag, filterActive]);

  const fetchTags = async () => {
    try {
      const response = await proxyListAPI.getTags();
      setTags(response.data);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const fetchProxyLists = async () => {
    try {
      setLoading(true);
      const isActive = filterActive === 'all' ? undefined : filterActive === 'active';
      const response = await proxyListAPI.getAll(selectedTag, isActive);
      setProxyLists(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this proxy list?')) {
      return;
    }

    try {
      await proxyListAPI.delete(id);
      fetchProxyLists();
    } catch (err) {
      alert('Failed to delete proxy list: ' + err.message);
    }
  };

  const getActiveProxiesCount = (proxyList) => {
    return proxyList.proxies.filter(p => p.isActive).length;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>Proxy Lists</h2>
          <Link to="/proxy-lists/create" className="btn btn-primary">
            Create New Proxy List
          </Link>
        </div>

        <div className="grid grid-2 mb-3">
          <div className="form-group">
            <label>Filter by Tag</label>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
            >
              <option value="">All Tags</option>
              {tags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            Error: {error}
          </div>
        )}

        {proxyLists.length === 0 ? (
          <div className="alert alert-info">
            No proxy lists found. Create your first proxy list to get started.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Proxies</th>
                  <th>Active Proxies</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {proxyLists.map(proxyList => (
                  <tr key={proxyList._id}>
                    <td>
                      {proxyList.name}
                    </td>
                    <td>{proxyList.description || '-'}</td>
                    <td>{proxyList.proxies.length}</td>
                    <td>
                      <span className="badge badge-info">
                        {getActiveProxiesCount(proxyList)} / {proxyList.proxies.length}
                      </span>
                    </td>
                    <td>
                      {proxyList.tags && proxyList.tags.length > 0 ? (
                        proxyList.tags.map(tag => (
                          <span key={tag} className="badge badge-secondary mr-1">
                            {tag}
                          </span>
                        ))
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`badge ${proxyList.isActive ? 'badge-success' : 'badge-secondary'}`}>
                        {proxyList.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <Link
                          to={`/proxy-lists/${proxyList._id}`}
                          className="btn btn-sm btn-secondary"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(proxyList._id)}
                          className="btn btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3">
          <p className="text-muted">
            Total: {proxyLists.length} {proxyLists.length === 1 ? 'list' : 'lists'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProxyListsList;
